import React, { ReactNode } from "react";
import htmlParse from "html-react-parser";
import Button from "../../../Foundation/Button/Button";
import {
  _LandingHeaderCtaWrapper,
  _LandingHeaderRoot,
  _LandingHeaderSvgUploadLogo,
} from "./LandingHeader.styled";
import HighlightedTypography from "../../HighlightedTypography/HighlightedTypography";
import { ImageResponsive } from "../../Image";
import { ImageFit } from "../../Image/interface/ImageData";
import Typography from "../../../Foundation/Typography/Typography";
import LandingSectionBase from "../LandingSectionBase/LandingSectionBase";
import { isEmptyImageId } from "../../Image/Image.utils";
import { logoSvgBase } from "./LandingHeader.config";
import { AvailableLanguage } from "../../../Interface/logoLang";
import useLogoSvg from "./useLogoSvg";

export interface ILogoData {
  logoId?: string;
  svgLogo?: keyof typeof logoSvgBase;
  svgLogoFile?: string;
  logoSource?: "image" | "svg" | "svg_upload";
  lang?: AvailableLanguage;
}

export interface ILandingHeaderProps extends ILogoData {
  image?: string;
  /** Rendu custom colonne hero (image/vidéo responsive BO). */
  renderHeroVisual?: React.ReactNode | (() => React.ReactNode);
  title?: string;
  subtitle?: string;
  description?: string;
  ctaLabel: string;
  hideCta?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  /**
   * URL du CTA. Si définie, le CTA est rendu en `<a href>` (meilleur SEO) ;
   * sinon on retombe sur un `<button>`. Le `onClick` reste appelé dans les deux cas.
   */
  ctaUrl?: string;
  backgroundImage?: {
    desktop?: string;
    mobile?: string;
  };
  /** Couche derrière le contenu (vidéo de fond). */
  backgroundBehindContainer?: ReactNode;
  imageLeft?: boolean;
}

const LandingHeader = (props: ILandingHeaderProps) => {
  const {
    logoId,
    image,
    renderHeroVisual,
    subtitle,
    title,
    description,
    ctaLabel = "Je découvre",
    hideCta = false,
    onClick,
    ctaUrl,
    backgroundImage,
    backgroundBehindContainer,
    imageLeft = false,
    logoSource = "image",
    svgLogo,
    svgLogoFile,
    lang,
  } = props;
  const { logoSvgSrc } = useLogoSvg(svgLogo, lang);
  const hasLogo = logoId && !isEmptyImageId(logoId);
  const hasTopContent =
    (logoSource === "svg_upload" && svgLogoFile) ||
    (logoSource === "image" && hasLogo) ||
    (svgLogo && logoSvgSrc);

  return (
    <>
      <LandingSectionBase
        boxContainer={true}
        backgroundImage={backgroundImage}
        backgroundBehindContainer={backgroundBehindContainer}
        renderImage={renderHeroVisual}
        image={renderHeroVisual ? undefined : image}
        onImageClick={onClick}
        imageLeft={imageLeft}
        topContent={
          !hasTopContent ? undefined : (
            <>
              {logoSource === "svg_upload" ? (
                <_LandingHeaderSvgUploadLogo src={svgLogoFile} alt="" />
              ) : logoSource === "image" ? (
                logoId &&
                !isEmptyImageId(logoId) && (
                  <>
                    <span className="landing-header__head__intro js-anchor">
                      <ImageResponsive
                        alt=""
                        desktop={{
                          image: {
                            id: logoId,
                            fit: ImageFit.CONTAIN,
                            quality: 80,
                            width: 366,
                            height: "auto",
                          },
                          imgStyle: {
                            width: "100%",
                            height: "auto",
                            maxWidth: 366,
                            objectFit: "contain",
                          },
                        }}
                        mobile={{
                          image: {
                            id: logoId,
                            fit: ImageFit.CONTAIN,
                            quality: 100,
                            outputPixelDensities: [1, 2],
                            width: 210,
                            height: "auto",
                          },
                          imgStyle: {
                            width: "100%",
                            height: "auto",
                            maxWidth: 210,
                            objectFit: "contain",
                          },
                        }}
                      />
                    </span>
                  </>
                )
              ) : (
                svgLogo &&
                logoSvgSrc && (
                  <span
                    className="landing-header__head__intro js-anchor"
                    style={{ display: "inline-block", marginBottom: 8 }}
                  >
                    <img
                      src={logoSvgSrc}
                      alt=""
                      style={{ width: "100%", maxWidth: 210 }}
                    />
                  </span>
                )
              )}
            </>
          )
        }
        bottomContent={
          <div>
            {title && (
              <HighlightedTypography
                highlightedText={subtitle}
                // highlightedColor={"yellow"}
                variant="h4"
                className="landing-header__title title"
                mb={21}
              >
                {title}
              </HighlightedTypography>
            )}
            <Typography variant="subtitle2" className="landing-header__intro">
              {htmlParse(description + "")}
            </Typography>
            {!hideCta && (
              <_LandingHeaderCtaWrapper className="landing-header__head__cta">
                <Button
                  variant="champagne"
                  onClick={onClick}
                  {...(ctaUrl ? { as: "a", href: ctaUrl } : {})}
                >
                  {ctaLabel}
                </Button>
              </_LandingHeaderCtaWrapper>
            )}
          </div>
        }
      />
    </>
  );
};

export default LandingHeader;
